<template>
  <BaseCard style="scroll-margin-top: calc(4rem + var(--sat))">
    <BaseForm
      ref="formComponent"
      class="space-y-5"
      :method="onFormSubmit"
      :reset-on-submit="false"
    >
      <div class="font-bold md:text-lg">
        {{ $t('showcases.profile_data') }}
      </div>
      <div class="flex gap-6 flex-wrap md:flex-nowrap">
        <BaseFormField required>
          <template #label>{{ $t('profile.first_name') }}</template>
          <template #input>
            <BaseInput
              v-model="form.first_name"
              name="first_name"
              rules="name|required"
              :placeholder="$t('profile.first_name')"
              :disabled="isDisabled"
            />
          </template>
        </BaseFormField>
        <BaseFormField required>
          <template #label>{{ $t('profile.last_name') }}</template>
          <template #input>
            <BaseInput
              v-model="form.last_name"
              name="last_name"
              rules="name|required"
              :placeholder="$t('profile.last_name')"
              :disabled="isDisabled"
            />
          </template>
        </BaseFormField>
      </div>
      <div class="flex gap-6 flex-wrap md:flex-nowrap">
        <BaseFormField required>
          <template #label>{{ $t('profile.job') }}</template>
          <template #input>
            <BaseInput
              v-model="form.job"
              name="job"
              rules="required"
              :placeholder="$t('profile.job')"
              :disabled="isDisabled"
            />
          </template>
        </BaseFormField>
        <BaseFormField required>
          <template #label>{{ $t('profile.whatsapp') }}</template>
          <template #input>
            <BaseInput
              v-model="form.whatsapp"
              name="whatsapp"
              rules="required"
              :placeholder="$t('profile.whatsapp_description')"
              :disabled="isDisabled"
            />
          </template>
        </BaseFormField>
      </div>
      <div class="flex gap-6 flex-wrap md:flex-nowrap">
        <BaseFormField required>
          <template #label>{{ $t('profile.country') }}</template>
          <template #input>
            <BaseSelectAsync
              v-model="form.country_id"
              :placeholder="$t('profile.select_country')"
              name="country"
              required
              :initial="initialCountry"
              :fetch="getCountriesAutocomplete"
              :disabled="isDisabled || isLoading"
              @change="onCountryChange"
            />
          </template>
        </BaseFormField>
        <BaseFormField>
          <template #label>{{ $t('profile.city') }}</template>
          <template #input>
            <BaseSelectAsync
              ref="citySelector"
              v-model="form.city_id"
              :placeholder="$t('profile.select_city')"
              name="city"
              :initial="initialCity"
              :fetch="fetchCitiesAutocomplete"
              :disabled="isDisabled || isLoading"
            />
          </template>
        </BaseFormField>
      </div>
      <input type="submit" hidden />
    </BaseForm>
  </BaseCard>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import BaseForm from '~/components/base/BaseForm.vue'
import type { UserProfileUpdate } from '~/models/users'
import { useUsers } from '~/stores/users'
import { useAuth } from '~/stores/auth'
import { useNotifications } from '~/stores/notifications'
import type { OptionInterface } from '~/components/base/controls/use-simple-input'
import BaseSelectAsync from '~/components/base/controls/BaseSelectAsync.vue'
import { toAutocomplete } from '~/utils/autocomplete'

const { pushNotification } = useNotifications()
const usersStore = useUsers()
const authStore = useAuth()
const { t } = useI18n()
const { getCitiesAutocomplete, getCountriesAutocomplete, detectGeo } = useGeo()

interface Props {
  externalProcessing?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  externalProcessing: false,
})

const emit = defineEmits<{
  (e: 'loading', value: boolean): void
  (e: 'submitRequest', value: void): void
}>()

defineExpose({
  submit,
  validate,
})

const isLoading = ref(false)
const internalProcessing = ref(false)

const isDisabled = computed(() => {
  return props.externalProcessing || internalProcessing.value
})

const formComponent = ref<InstanceType<typeof BaseForm> | null>(null)

const form = reactive<UserProfileUpdate>({
  first_name: '',
  job: '',
  last_name: '',
  country_id: undefined,
  city_id: undefined,
  whatsapp: undefined,
})

function fillForm() {
  Object.keys(form).forEach((key) => {
    form[key as keyof typeof form] = authStore.profile?.[key]
  })
}

fillForm()

const initialCountry = ref<OptionInterface | undefined>(
  authStore.profile?.country
    ? toAutocomplete(authStore.profile.country)
    : undefined
)

const initialCity = ref<OptionInterface | undefined>(
  authStore.profile?.city ? toAutocomplete(authStore.profile.city) : undefined
)

watch(isLoading, (value) => {
  emit('loading', value)
})

onMounted(async () => {
  if (form.country_id && form.city_id) {
    isLoading.value = false
    return
  }

  isLoading.value = true

  const detectedGeo = await detectGeo()
  if (!form.country_id && detectedGeo.country) {
    if (!form.city_id || form.city_id === detectedGeo.city?.value) {
      form.country_id = String(detectedGeo.country.value) || undefined
      initialCountry.value = detectedGeo.country
    }
  }

  if (!form.city_id && detectedGeo.city) {
    if (!form.country_id || form.country_id === detectedGeo.country?.value) {
      form.city_id = Number(detectedGeo.city.value) || undefined
      initialCity.value = detectedGeo.city
    }
  }

  isLoading.value = false
})

function fetchCitiesAutocomplete(query: string) {
  return form.country_id ? getCitiesAutocomplete(form.country_id, query) : []
}

const citySelector = ref<InstanceType<typeof BaseSelectAsync> | null>(null)

function onCountryChange() {
  const cityInput = citySelector.value

  form.city_id = undefined
  initialCity.value = undefined
  cityInput?.clear()
  cityInput?.clearOptions()
}

function onFormSubmit(): void {
  emit('submitRequest')
}

async function validate(): Promise<boolean> {
  return (await formComponent.value?.validate())?.valid || false
}

async function submit(): Promise<void> {
  if (internalProcessing.value || !(await validate())) {
    return
  }

  try {
    internalProcessing.value = true

    await authStore.updateProfile(form)
    await usersStore.reloadUser()

    fillForm()
  } catch (error: any) {
    pushNotification({
      title: t(`notifications.profile.update_error`),
      theme: 'destructive',
    })
    useLogError(error, true)
  } finally {
    internalProcessing.value = false
  }
}
</script>
